import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Mail } from "lucide-react";
import { api } from "../lib/api";
import { trackCtaClick } from "../lib/analytics";
import { SideNav } from "../components/SideNav";
import { CommandBar } from "../components/CommandBar";
import { Presentation } from "../components/Presentation";
import { Logo } from "../components/Logo";
import { SECTION_COMPONENTS } from "../Sections";

export default function Portal() {
  const [content, setContent] = useState(null);
  const [investor, setInvestor] = useState(() => {
    try { return JSON.parse(localStorage.getItem("s360_investor") || "null"); }
    catch { return null; }
  });
  const [access, setAccess] = useState("checking"); // checking | ok | denied
  const [checkingSlow, setCheckingSlow] = useState(false);
  const [active, setActive] = useState("welcome");
  const [viewedIds, setViewedIds] = useState(new Set());
  const [presenting, setPresenting] = useState(false);
  const [slide, setSlide] = useState(0);
  const refs = useRef({});

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
  }, []);

  // Investor session resolution (magic link). If no investor cookie, try founder preview.
  useEffect(() => {
    const slowTimer = setTimeout(() => setCheckingSlow(true), 6000);
    (async () => {
      try {
        const r = await api.get("/investor/me");
        localStorage.setItem("s360_investor", JSON.stringify(r.data));
        setInvestor(r.data);
        setAccess("ok");
      } catch {
        try {
          const r2 = await api.get("/investor/preview");
          // Founder preview — do not persist in localStorage so it stays founder-session-scoped
          setInvestor(r2.data);
          setAccess("ok");
        } catch {
          localStorage.removeItem("s360_investor");
          setAccess("denied");
        }
      } finally {
        clearTimeout(slowTimer);
      }
    })();
    return () => clearTimeout(slowTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!content || access !== "ok") return;
    const hash = window.location.hash.replace("#", "");
    if (hash) {
      const t = setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: "auto", block: "start" });
      }, 400);
      return () => clearTimeout(t);
    }
  }, [content, access]);

  const registerRef = useCallback((id, el) => { refs.current[id] = el; }, []);
  const onActive = useCallback((id) => {
    setActive(id);
    setViewedIds((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  const sections = useMemo(() => {
    if (!content) return [];
    const isFounder = Boolean(investor?.preview);
    return content.sections_order.filter((s) => {
      if (s.id === "sandbox" && !(content.sandbox?.url && investor?.sandbox_access)) return false;
      if (s.id === "business_model" && !(content.business_model?.url && investor?.business_model_access)) return false;
      // Appendix slides: hidden from investors unless promoted. Always visible to founders.
      if (s.appendix && !isFounder && !s.promoted) return false;
      return true;
    });
  }, [content, investor]);

  const data = useMemo(() => (content ? { ...content, investor } : null), [content, investor]);

  const progress = useMemo(() => {
    if (!sections.length) return 0;
    const idx = sections.findIndex((s) => s.id === active);
    return ((idx + 1) / sections.length) * 100;
  }, [active, sections]);

  const jump = (id) => {
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (!content || access === "checking") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="min-h-screen flex flex-col items-center justify-center gap-2 text-ftext3 font-mono-data text-sm px-6 text-center"
      >
        <span>{access === "checking" ? "Checking access…" : "Loading brief…"}</span>
        {checkingSlow && access === "checking" && (
          <span data-testid="portal-checking-slow" className="text-xs text-ftext3/80 max-w-sm leading-relaxed">
            Taking longer than usual. If this persists, please refresh the page or contact the Streamline360 team.
          </span>
        )}
      </div>
    );
  }

  if (access !== "ok") {
    return (
      <div data-testid="portal-invitation-required" className="min-h-screen flex items-center justify-center bg-ink px-6">
        <main role="main" aria-labelledby="invite-required-title" className="max-w-lg w-full border border-line bg-panel p-8">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider text-ftext2">
              <Lock className="h-4 w-4" /> Invitation only
            </div>
            <Logo className="h-6 w-auto" />
          </div>
          <div className="flex items-start gap-3 mb-4">
            <Mail className="w-6 h-6 text-sky mt-0.5 shrink-0" />
            <div>
              <h1 id="invite-required-title" className="font-heading font-black text-xl text-ftext leading-tight mb-1">This deck is invitation-only</h1>
              <p className="text-sm text-ftext2 leading-relaxed">Please open the Streamline Investor Pitch Deck using the personal link the Streamline360 team emailed to you. Every investor receives a unique link — no sign-in required.</p>
            </div>
          </div>
          <p className="text-xs text-ftext3 leading-relaxed">Can't find your invitation? Request a new link or contact the Streamline360 team.</p>
          <div className="mt-5 grid gap-2">
            <a
              data-testid="portal-request-invitation"
              href="mailto:investors@streamline360.ai?subject=Streamline360%20Investor%20Deck%20-%20New%20invitation%20request&body=Hello%20Streamline360%20team,%0A%0ACould%20you%20please%20send%20me%20a%20personal%20link%20to%20the%20investor%20deck?%0A%0AThank%20you."
              className="flex items-center justify-between border border-sky bg-sky/10 text-sky hover:bg-sky/20 px-4 py-3 font-mono-data text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Request a new invitation</span>
              <span aria-hidden>→</span>
            </a>
            <a
              data-testid="portal-contact-team"
              href="mailto:investors@streamline360.ai?subject=Streamline360%20-%20Investor%20deck%20access"
              className="flex items-center justify-between border border-line2 bg-panel hover:border-sky hover:text-sky text-ftext2 px-4 py-3 font-mono-data text-xs font-bold uppercase tracking-wider transition-colors"
            >
              <span>Contact the team</span>
              <span className="normal-case tracking-normal text-ftext3">investors@streamline360.ai</span>
            </a>
          </div>
          <div className="mt-6 pt-4 border-t border-line flex items-center justify-between">
            <span className="font-mono-data text-xs uppercase tracking-wider text-ftext3">Confidential — do not forward</span>
            <Link
              data-testid="founder-access-link"
              to="/login"
              className="font-mono-data text-xs uppercase tracking-wider text-ftext3 hover:text-sky transition-colors"
            >
              Founder access →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const common = { onActive, registerRef };

  const startPresenting = () => {
    const idx = Math.max(0, sections.findIndex((s) => s.id === active));
    setSlide(idx);
    setPresenting(true);
    trackCtaClick("present_open", { from: active });
  };

  return (
    <div className="min-h-screen bg-ink">
      {presenting && (
        <Presentation
          content={data}
          sections={sections}
          index={slide}
          setIndex={setSlide}
          onExit={() => setPresenting(false)}
        />
      )}
      <SideNav sections={sections} active={active} onJump={jump} progress={progress} viewedIds={viewedIds} />

      <div className="lg:pl-60">
        <CommandBar confidentialNotice={content.brand.confidential_notice} onPresent={startPresenting} />

        <main data-testid="portal-main">
          {sections.map((s) => {
            const C = SECTION_COMPONENTS[s.id];
            return C ? <C key={s.id} data={data} common={common} /> : null;
          })}
        </main>

        <footer className="border-t border-line px-6 py-8 md:px-14 lg:px-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 flex items-center gap-2">
            <Lock className="w-3.5 h-3.5" />
            {content.brand.confidential_notice}
          </div>
          <Link
            data-testid="founder-login-link"
            to="/login"
            className="font-mono-data text-xs uppercase tracking-wider text-ftext3 hover:text-sky transition-colors"
          >
            Founder access →
          </Link>
        </footer>
      </div>
    </div>
  );
}
